// src/components/superadmin/SuperAdminCaseView.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import {
  BiBriefcase,
  BiFile,
  BiMap,
  BiUser,
  BiBuilding,
  BiArrowBack,
  BiTimeFive,
  BiListUl,
} from "react-icons/bi"; // Add necessary icons
import { API_BASE_URL } from "../utils/urls.js";
import LoadingSpinner from "../common/LoadingSpinner"; // Assuming you have this

// Reusable Detail Item Component
const DetailItem = ({ label, value, isLink = false, linkTo = "#" }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="grid grid-cols-3 gap-2 py-1">
      <dt className="text-sm font-medium text-gray-500">{label}:</dt>
      <dd className="col-span-2 text-sm text-gray-800">
        {isLink ? (
          <Link to={linkTo} className="text-blue-600 hover:underline">
            {value}
          </Link>
        ) : (
          value
        )}
      </dd>
    </div>
  );
};

// Reusable Section Component
const DetailSection = ({ title, icon, children }) => (
  <div className="bg-white p-4 rounded-lg shadow border border-gray-100">
    <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center border-b pb-2">
      {React.cloneElement(icon, { className: "w-5 h-5 mr-2 text-blue-600" })}
      {title}
    </h2>
    <div className="space-y-2">{children}</div>
  </div>
);

const SuperAdminCaseView = () => {
  const { id } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCaseDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token");
        // Use the new super-admin case detail endpoint
        const response = await axios.get(
          `${API_BASE_URL}/super-admin/cases/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setCaseData(response.data);
      } catch (err) {
        console.error("Error fetching case details:", err);
        setError(err.response?.data?.message || "Failed to load case details.");
      } finally {
        setLoading(false);
      }
    };
    fetchCaseDetails();
  }, [id]);

  const formatDate = (dateString) =>
    dateString ? new Date(dateString).toLocaleString() : "N/A";
  const formatBool = (value) => (value ? "Yes" : "No");

  if (loading)
    return (
      <div className="p-10 flex justify-center">
        <LoadingSpinner />
      </div>
    );
  if (error)
    return (
      <div className="p-10 text-center text-xl text-red-600 bg-red-50 rounded-lg">
        {error}
      </div>
    );
  if (!caseData)
    return (
      <div className="p-10 text-center text-lg text-gray-500">
        Case not found.
      </div>
    );

  // Destructure related data safely
  const {
    servees = [],
    subjects = [],
    documents = [],
    media = [],
    case_activity = [],
    assigned_agents = [],
    // custom_fields = {}, // Add if fetched
    client_full_name,
    client_email_address,
    client_phone_number,
    organization_id,
    organization_name,
  } = caseData;

  const isPiCase = caseData.is_pi === 1;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      {/* Back Link */}
      <Link
        to="/super-admin/cases"
        className="text-blue-600 hover:underline mb-4 inline-block"
      >
        &larr; Back to All Cases
      </Link>

      {/* Case Header */}
      <header className="bg-white p-5 rounded-lg shadow border border-gray-100">
        <h1 className="text-2xl font-bold text-gray-800">
          {caseData.case_name}
        </h1>
        <p className="text-gray-600">Case Number: {caseData.case_number}</p>
        <p className="text-sm text-gray-500">
          Type: {isPiCase ? "Private Investigation" : "Process Service"}
        </p>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <span
            className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
              caseData.status === "Open"
                ? "bg-blue-100 text-blue-800"
                : caseData.status === "Closed"
                ? "bg-green-100 text-green-800"
                : "bg-yellow-100 text-yellow-800"
            }`}
          >
            Status: {caseData.status}
          </span>
          <span>Created: {formatDate(caseData.created_at)}</span>
          {caseData.due_date && (
            <span>Due: {formatDate(caseData.due_date)}</span>
          )}
        </div>
      </header>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column (Case Details) */}
        <div className="lg:col-span-2 space-y-6">
          <DetailSection title="Case Information" icon={<BiBriefcase />}>
            <DetailItem label="Service Type" value={caseData.service_type} />
            <DetailItem
              label="Jurisdiction"
              value={caseData.court_jurisdiction}
            />
            <DetailItem
              label="Client Ref #"
              value={caseData.client_reference_number}
            />
            <DetailItem label="Pages" value={caseData.number_of_pages} />
            <DetailItem label="Description" value={caseData.description} />
            {isPiCase && (
              <DetailItem label="Objectives" value={caseData.objectives} />
            )}
            {isPiCase && (
              <DetailItem
                label="Relevant Dates"
                value={caseData.relevant_dates}
              />
            )}
            {isPiCase && (
              <DetailItem
                label="Known Locations"
                value={caseData.known_locations}
              />
            )}
          </DetailSection>

          <DetailSection title="Client & Organization" icon={<BiBuilding />}>
            <DetailItem
              label="Organization"
              value={organization_name || "N/A"}
              isLink={!!organization_id}
              linkTo={`/organizations/view/${organization_id}`}
            />
            <DetailItem label="Client Name" value={client_full_name || "N/A"} />
            <DetailItem label="Client Email" value={client_email_address} />
            <DetailItem label="Client Phone" value={client_phone_number} />
            {/* Add more client_info fields if needed */}
          </DetailSection>

          {/* Servees or Subjects Section */}
          <DetailSection
            title={isPiCase ? "Investigation Subjects" : "Service Servees"}
            icon={<BiMap />}
          >
            {isPiCase ? (
              subjects.length > 0 ? (
                subjects.map((subj, index) => (
                  <div
                    key={subj.id || index}
                    className="p-3 bg-gray-50 rounded border text-sm"
                  >
                    <p className="font-semibold">{subj.full_name}</p>
                    <p>Address: {subj.primary_address}</p>
                    {/* Add more subject details */}
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500">No subjects found.</p>
              )
            ) : servees.length > 0 ? (
              servees.map((srv, index) => (
                <div
                  key={srv.id || index}
                  className="p-3 bg-gray-50 rounded border text-sm"
                >
                  <p className="font-semibold">{srv.full_name}</p>
                  <p>
                    Address: {srv.primary_address}
                    {srv.apartment_unit
                      ? `, Unit ${srv.apartment_unit}`
                      : ""}, {srv.city_state_zip}
                  </p>
                  <p>Aliases: {srv.aliases || "N/A"}</p>
                  {/* Add more servee details */}
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No servees found.</p>
            )}
          </DetailSection>

          {/* Add other sections as needed (e.g., Custom Fields) */}
        </div>

        {/* Right Column (Related Lists) */}
        <div className="lg:col-span-1 space-y-6">
          <DetailSection title="Assigned Agents" icon={<BiUser />}>
            {assigned_agents.length > 0 ? (
              assigned_agents.map((agent) => (
                <p key={agent.user_id} className="text-sm text-gray-800">
                  <Link
                    to={`/super-admin/users/${agent.user_id}`}
                    className="text-blue-600 hover:underline"
                  >
                    {agent.assigned_user_name}
                  </Link>
                </p>
              ))
            ) : (
              <p className="text-sm text-gray-500 italic">Unassigned</p>
            )}
          </DetailSection>

          <DetailSection title="Documents" icon={<BiFile />}>
            <ul className="space-y-1 max-h-60 overflow-y-auto">
              {documents.length > 0 ? (
                documents.map((doc) => (
                  <li key={doc.id} className="text-sm text-gray-700 truncate">
                    {doc.title || doc.file_name}
                  </li>
                ))
              ) : (
                <p className="text-sm text-gray-500 italic">No documents.</p>
              )}
            </ul>
          </DetailSection>

          <DetailSection title="Media" icon={<BiFile />}>
            <ul className="space-y-1 max-h-60 overflow-y-auto">
              {media.length > 0 ? (
                media.map((m) => (
                  <li key={m.id} className="text-sm text-gray-700 truncate">
                    {m.title || m.file_name} ({m.file_type})
                  </li>
                ))
              ) : (
                <p className="text-sm text-gray-500 italic">No media.</p>
              )}
            </ul>
          </DetailSection>

          <DetailSection title="Activity Log" icon={<BiListUl />}>
            <ul className="space-y-2 max-h-80 overflow-y-auto text-xs">
              {case_activity.length > 0 ? (
                case_activity.map((log) => (
                  <li key={log.id} className="p-2 bg-gray-50 rounded border">
                    <p className="font-medium text-gray-700">
                      {log.description}
                    </p>
                    <p className="text-gray-500">
                      {formatDate(log.created_at)} by{" "}
                      {log.user_name || "System"}
                    </p>
                  </li>
                ))
              ) : (
                <p className="text-sm text-gray-500 italic">No activity yet.</p>
              )}
            </ul>
          </DetailSection>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminCaseView;
